import { Alert, Avatar, Button, Modal, Card, Col, Flex, Row, Typography, Empty, Slider, InputNumber, notification, FloatButton, Select, App, Image, Space, Skeleton, Radio, Divider, RadioChangeEvent, Spin, Input, Badge, Dropdown } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { User } from '@models/user';
import { Artwork } from '../../../data/models/artwork';
import { ArtistInventoryPlacard, Placards } from '../../../data/models/placards';
import { DeleteOutlined, EditOutlined, MoreOutlined, UserOutlined } from '@ant-design/icons';
import { makeRequest } from '@helpers/axios-manipulator';
import * as S from './styles';
import Placard from '../../components/Placard';
import html2pdf from 'html2pdf.js';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading } from '../../../redux/loadingSlicer';
import { RootState } from '../../../redux/appState';
import PlacardsTable from '../../components/PlacardsTable';

const { Text } = Typography;

function PlacardsScreen() {
	const { message, notification } = App.useApp();
	const user = useSelector((state: RootState) => state.user.user);
	const [data, setData] = useState<User>({} as User);

	const [allArtworks, setAllArtworks] = useState<ArtistInventoryPlacard[]>([]);
	const [artworkQuery, setArtworkQuery] = useState('');
	const [placardQuery, setPlacardQuery] = useState('');
	const [loadingArtworks, setLoadingArtworks] = useState(false);

	// --- User searchbox state and logic ---
	type UserOption = { label: string; value: string };
	const [userOptions, setUserOptions] = useState<UserOption[]>([]);
	const [allUsers, setAllUsers] = useState<User[]>([]);
	const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

	const fetchPlacardsInventory = () => {
		setLoadingArtworks(true);
		return makeRequest({
			origin: window.location.origin,
			method: 'get',
			url: `/api/placards-inventory`,
		})
		.then((res: ArtistInventoryPlacard[]) => {
			// Deduplicate data based on artwork ID.
			const uniqueMap = new Map<string, ArtistInventoryPlacard>();
			res.forEach((item) => {
				if (!item.id) return;
				const id = String(item.id).trim(); // Normalize ID
				const existing = uniqueMap.get(id);
				
				if (!existing) {
					uniqueMap.set(id, item);
				} else {
					// If existing entry has no placard, but this one does, update it.
					if ((!existing.placard_id && item.placard_id) || (existing.placard_id && item.placard_id && item.updated_date && existing.updated_date && new Date(item.updated_date) > new Date(existing.updated_date))) {
						uniqueMap.set(id, item);
					}
				}
			});
			
			const processedArtworks = Array.from(uniqueMap.values()).map(item => ({
				...item,
				code: item.placard_id || '',
				display_name: item.artist_display_name || item.artist_username || 'N/A',
				artwork_id: item.id,
				artwork_type: item.copy_reference_id ? 'PRINT' : 'original',
				placard_status: item.placard_id ? (item.current_owner !== item.created_by_artist_id ? 'acquisitions' : 'inventory') : 'unlinked'
			}));

			setAllArtworks(processedArtworks);

			const artistMap: Record<string, User> = {};
			processedArtworks.forEach((item: any) => {
				const artistId = item.created_by_artist_id;
				if (artistId && !artistMap[artistId]) {
					artistMap[artistId] = {
						id: artistId,
						display_name: item.artist_display_name,
						username: item.artist_username,
					} as User;
				}
			});
			const artists = Object.values(artistMap);
			setAllUsers(artists);
			setUserOptions(artists.map((user: User) => ({
				label: user.display_name || user.username || user.id,
				value: user.id,
			})));

			// Ensure we only show items with a valid placard_id in the placards view
			const placardsViewData = processedArtworks.filter((item: any) => item.placard_id);
			setPlacards(placardsViewData);
			setFilteredPlacards(placardsViewData);
		})
		.catch((err) => {
			console.error("Error fetching placards inventory:", err);
			setAllArtworks([]);
			setUserOptions([]);
			setPlacards([]);
			setFilteredPlacards([]);
		})
		.finally(() => setLoadingArtworks(false));
	};

	// Fetch all data on mount
	useEffect(() => {
		if (user) {
			fetchPlacardsInventory();
		}
	}, [user]);

	// Filter artworks based on user selection and search queries
	const artworksToDisplay = useMemo(() => {
		let filtered = allArtworks;

		if (selectedUserId) {
			filtered = filtered.filter(art => art.created_by_artist_id === selectedUserId);
		}

		if (artworkQuery) {
			const lowercasedQuery = artworkQuery.toLowerCase();
			filtered = filtered.filter(art => (art.title || '').toLowerCase().includes(lowercasedQuery));
		}

		if (placardQuery) {
			const lowercasedQuery = placardQuery.toLowerCase();
			filtered = filtered.filter(art => (art.placard_id || '').toLowerCase().includes(lowercasedQuery));
		}

		return filtered;
	}, [selectedUserId, allArtworks, artworkQuery, placardQuery, user]);

	const handleArtworkFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
		setArtworkQuery(e.target.value);
	};
	const handlePlacardFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
		setPlacardQuery(e.target.value);
	};

	const generatePlacards = async () => {
		if (!selectedArtworks.length) return;
		setInternalLoading(true);
		let placards: Placards[] = [];
		for (const e of selectedArtworks) {
			try {
				const response = await makeRequest({
					origin: window.location.origin,
					method: 'post',
					url: '/api-generate-placard',
					data: { artworkId: e, isEnabled: true },
					headers: {
						'Content-Type': 'application/json',
					},
				});
				placards.push({ ...response });
			} catch (error) {
				console.error('Error generating placards:', error);
			}
		}
		setGeneratedLinkedPlacs(placards);
		setOpenModal(true);
		setInternalLoading(false);
		message.success('Placards generated successfully');
		fetchPlacardsInventory();
		setSelectedArtworks([]);
	};
	const downloadPlacardAsPdf = () => {
		if (!placardRef.current) return;
		html2pdf().from(placardRef.current).save('placards.pdf');
	};
	const saveEditedPlacard = async () => {
		if (!selectedEditArtwork.id) return;
		setInternalLoading(true);
		try {
			if (editForm.placard_id) {
				await makeRequest({
					origin: window.location.origin,
					method: 'put',
					url: '/api-update-placard',
					data: {
						placard: {
							code: editForm.placard_id,
							is_enabled: true,
							artwork_id: selectedEditArtwork.id,
						},
					},
					headers: {},
				});
			}
			if (
				selectedEditArtwork.placard_id &&
				selectedEditArtwork.placard_id !== editForm.placard_id
			) {
				await makeRequest({
					origin: window.location.origin,
					method: 'put',
					url: '/api-update-placard',
					data: {
						placard: {
							code: selectedEditArtwork.placard_id,
							is_enabled: false,
							artwork_id: null,
						},
					},
					headers: {},
				});
			}
			fetchPlacardsInventory();
			setEditPlacardModal(false);
			setSelectedEditArtwork({} as Artwork);
			setSelectedArtworks([]);
			setInternalLoading(false);
			message.success('Placard assignment updated successfully');
		} catch (err) {
			setInternalLoading(false);
			message.error('Failed to update placard assignment');
		}
	};
	const handleEditFormChange = (field: string, value: any) => {
		setEditForm((prev) => ({ ...prev, [field]: value }));
	};
	const generateEmptyPlacards = async (exp = false) => {
		dispatch(setLoading(true));
		setOpenEmptyPlacardsModal(false);
		try {
			const response = await makeRequest({
				origin: window.location.origin,
				method: 'post',
				headers: { 'Content-Type': 'application/json' },
				url: '/api-generate-empty-placards',
				data: { times },
			});
			if (exp) {
				const modifiedArray = await response.generatedPlacards.map((item: any) => ({
					code: item.generate_placards_ids
				}));
				setGeneratedLinkedPlacs(modifiedArray);
				setOpenModal(true);
				dispatch(setLoading(false));
			} else {
				dispatch(setLoading(false));
			}
			notification.success({
				message: 'Success!',
				description: 'Not assigned placards created!',
				placement: 'topRight',
				title: 'Placards Generated',
			});
		} catch (error) {
			dispatch(setLoading(false));
			notification.error({
				message: 'Error!',
				description: 'Failed to generate empty placards',
				placement: 'topRight',
				title: 'Placard Generation Failed',
			});
		}
	};

	const dispatch = useDispatch();
	const [openEmptyPlacardsModal, setOpenEmptyPlacardsModal] = useState(false);
	const [times, setTimes] = useState(1);
	const [openModal, setOpenModal] = useState(false);
	const [generatedLinkedPlacs, setGeneratedLinkedPlacs] = useState<Placards[]>([]);
	const [internalLoading, setInternalLoading] = useState(false);
	const [placards, setPlacards] = useState<ArtistInventoryPlacard[]>([]);
	const [filteredPlacards, setFilteredPlacards] = useState<ArtistInventoryPlacard[]>([]);
	const [view, setView] = useState<'inventory' | 'placards'>('inventory');
	const [selectedArtworks, setSelectedArtworks] = useState<string[]>([]);
	const [editPlacardModal, setEditPlacardModal] = useState(false);
	const [selectedEditArtwork, setSelectedEditArtwork] = useState<Artwork>({} as Artwork);
	const [editForm, setEditForm] = useState<{ title: string; placard_id: string; is_sale_enabled: boolean }>({ title: '', placard_id: '', is_sale_enabled: false });
	const [availablePlacards, setAvailablePlacards] = useState<Placards[]>([]);
	const [unlinkPlacardModal, setUnlinkPlacardModal] = useState(false);
	const [unlinkPlacardCode, setUnlinkPlacardCode] = useState<string | null>(null);

	useEffect(() => {
		if (!editPlacardModal) return;
		const getAvailablePlacards = async () => {
			try {
				const response = await makeRequest({
					origin: window.location.origin,
					method: 'get',
					url: `/api-available-placards`,
					headers: {},
				});
				setAvailablePlacards(response);
			} catch (error) {
				console.error('Error fetching available placards:', error);
			}
		};
		getAvailablePlacards();
	}, [editPlacardModal]);

	const placardRef = useRef<HTMLDivElement>(null);

	const onChangeView = (e: RadioChangeEvent) => {
		setView(e.target.value);
		setSelectedArtworks([]);
	};

	const unlinkPlacard = async (placardCode: string) => {
		if (!placardCode) return;
		setInternalLoading(true);
		try {
			await makeRequest({
				origin: window.location.origin,
				method: 'put',
				url: '/api-update-placard',
				data: {
					placard: {
						code: placardCode,
						is_enabled: false,
						artwork_id: null,
					},
				},
				headers: {},
			});
			await fetchPlacardsInventory();
			setInternalLoading(false);
			setUnlinkPlacardModal(false);
			setUnlinkPlacardCode(null);
			message.success('Placard unlinked successfully');
		} catch (error) {
			setInternalLoading(false);
			message.error('Failed to unlink placard');
		}
	};

	return (
		<>
			<S.PlacardsContainer>
				<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
					<Button size='large' type="primary" onClick={() => setOpenEmptyPlacardsModal(true)}>
						Generate Empty placards
					</Button>
				</div>
				<Flex align="center" gap="middle" vertical>
					<Row style={{ width: '100%' }}>
						<Col span={24} style={{ textAlign: 'center', marginBottom: 16 }}>
							<Radio.Group
								onChange={onChangeView}
								options={[
									{ label: 'Inventory view', value: 'inventory' },
									{ label: 'Placards View', value: 'placards' },
								]}
								value={view}
								optionType="button"
								buttonStyle="solid"
							/>
						</Col>
					</Row>
					<Row gutter={[16, 16]} style={{ width: '100%' }}>
						{(user?.type === 0 || view === 'inventory') &&
							<Col xs={24} md={8}>
								<Select
									showSearch
									className='mb-5'
									style={{ width: '100%' }}
									placeholder="Select user"
									value={selectedUserId}
									options={userOptions}
									optionFilterProp="label"
									onChange={val => {
										setSelectedUserId(val);
										const userObj = allUsers.find(u => u.id === val);
										if (userObj) setData(userObj);
									}}
									filterOption={(input, option) =>
										(option?.label as string)?.toLowerCase().includes(input.toLowerCase())
									}
								/>
							</Col>
						}
						{view === 'inventory' && (
							<>
								<Col xs={24} md={8}>
									<Input.Search
										placeholder="Search by artwork title"
										allowClear
										onChange={handleArtworkFilter}
										style={{ marginBottom: 16 }}
									/>
								</Col>
								<Col xs={24} md={8}>
									<Input.Search
										placeholder="Search by placard ID"
										allowClear
										onChange={handlePlacardFilter}
										style={{ marginBottom: 16 }}
									/>
								</Col>
							</>
						)}
					</Row>
					<Row style={{ width: '100%' }}>
						<Col span={24}>
							{loadingArtworks ? (
								<div style={{ textAlign: 'center', margin: '2rem 0' }}>
									<Spin size="large" />
								</div>
							) : view === 'placards' ? (
								<PlacardsTable
									artworks={filteredPlacards}
									userId={selectedUserId}
									unlinkPlacard={unlinkPlacard}
									reRenderPlacards={fetchPlacardsInventory}
									loadingArtworks={loadingArtworks}
								/>
							) : (
								<>
									<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 16 }}>
										{selectedArtworks.length <= 1 && (
											<Button
												type="primary"
												style={{ background: '#1A093F', borderColor: '#1A093F', color: '#fff' }}
												disabled={selectedArtworks.length !== 1}
												onClick={() => {
													if (selectedArtworks.length === 1) {
														const selected = artworksToDisplay.find(a => a.id === selectedArtworks[0]);
														if (selected) {
															setSelectedEditArtwork(selected);
															setEditPlacardModal(true);
														}
													}
												}}
											>
												Assign to placard
											</Button>
										)}
										<Button
											type="primary"
											style={{ background: '#1A093F', borderColor: '#1A093F', color: '#fff' }}
											disabled={selectedArtworks.length === 0}
											onClick={generatePlacards}
										>
											Generate placards
										</Button>
									</div>
									<Row gutter={[16, 16]}>
										{artworksToDisplay.length > 0 ? (
											artworksToDisplay.map((art, idx) => (
												<Col key={art.id ?? idx} xs={24} sm={12} md={8} lg={6} xl={4}>
													<div style={{ position: 'relative' }}>
														<Card
															hoverable={!art.placard_id}
															style={{
																marginBottom: 16,
																borderRadius: 8,
																padding: '0.5rem',
																overflow: 'hidden',
																cursor: art.placard_id ? 'not-allowed' : 'pointer',
															}}
															cover={
																<Image
																	style={{ objectFit: 'cover', height: 130, borderRadius: '8px 8px 0 0' }}
																	preview={false}
																	src={art.image_url || ''}
																	alt={art.title || ''}
																/>
															}
															onClick={e => {
																if (art.placard_id) return;
																if (!(e.target as HTMLElement).closest('.selected-artwork-overlay')) {
																	setSelectedArtworks(
																		selectedArtworks.includes(art.id)
																			? selectedArtworks.filter(id => id !== art.id)
																			: [...selectedArtworks, art.id]
																	);
																}
															}}
														>
															<Card.Meta title={art.title} />
															<div>
																<Text type={art.is_sale_enabled ? 'success' : 'warning'}>
																	{art.is_sale_enabled ? 'Sale Enabled' : 'Not for Sale'}
																</Text><br />
																{art.artist_private === 1 || art.artist_private === true ? (
																	<Text type="warning">Artist Private</Text>
																) : (
																	<Text type="success">Artist Public</Text>
																)}
																<p className="my-0 flex">
																	<Text strong>
																		Placard Id: <span style={{ margin: 0 }}>{art.placard_id || <Text type="warning">Unassigned</Text>}</span>
																	</Text>
																</p>
																{typeof art.placard_id === 'string' && art.placard_id && (
																	<Dropdown
																		menu={{
																			items: [
																				{
																					key: 'edit',
																					icon: <EditOutlined style={{ fontSize: '16px' }} />, 
																					label: '',
																					onClick: (e: any) => {
																						setSelectedEditArtwork(art);
																						setEditPlacardModal(true);
																					},
																				},
																				{
																					key: 'unlink',
																					icon: <DeleteOutlined style={{ fontSize: '16px' }} />, 
																					label: '',
																					onClick: (e: any) => {
																						setUnlinkPlacardCode(art.placard_id);
																						setUnlinkPlacardModal(true);
																					},
																				},
																			],
																		}}
																		trigger={['click']}
																		getPopupContainer={(triggerNode: HTMLElement) => triggerNode.parentNode as HTMLElement}
																	>
																		<Button
																			icon={<MoreOutlined />}
																			type="text"
																			style={{ position: 'absolute', right: 1, bottom: 1, zIndex: 2000 }}
																			onClick={e => e.stopPropagation()}
																		/>
																	</Dropdown>
																)}
															</div>
														</Card>
														{selectedArtworks.includes(art.id) && (
															<div
																className="flex flex-col m-auto absolute bg-overlay-500 hover:bg-overlay-500 h-full top-0 z-10 cursor-pointer justify-center items-center desktop selected-artwork-overlay"
																onClick={e => {
																	e.stopPropagation();
																	setSelectedArtworks(selectedArtworks.filter(id => id !== art.id));
																}}
																style={{
																	width: 'calc(100% - 16px)',
																	left: '8px',
																	borderRadius: 8,
																	background: '#383838bf',
																	height: '100%',
																	top: 0,
																}}
															>
																<Alert title="Selected" type="success" showIcon style={{ borderRadius: 8 }} />
															</div>
														)}
													</div>
												</Col>
											))
										) : (
											<Col span={24} style={{ textAlign: 'center' }}>
												<Empty description="No artworks found." />
											</Col>
										)}
									</Row>
								</>
							)}
						</Col>
					</Row>
				</Flex>
				{/* Edit Artwork Modal */}
				<Modal
					open={editPlacardModal}
					title="Edit Placard"
					onCancel={() => setEditPlacardModal(false)}
					onOk={saveEditedPlacard}
					confirmLoading={internalLoading}
					okText="Save"
				>
					<div style={{ marginBottom: 16 }}>
						<Text strong>Artist Display Name:</Text>
						<Input
							value={(() => {
								if (selectedEditArtwork?.created_by_artist_id) {
									if (user && user.id === selectedEditArtwork.created_by_artist_id) {
										return user.display_name || user.username || '';
									}
									if (data && data.id === selectedEditArtwork.created_by_artist_id) {
										return data.display_name || data.username || '';
									}
								}
								return '';
							})()}
							readOnly
							placeholder="Artist Display Name"
							style={{ backgroundColor: '#f5f5f5', color: '#888' }}
						/>
					</div>
					<div style={{ marginBottom: 16 }}>
						<Text strong>Artwork ID:</Text>
						<Input
							value={selectedEditArtwork?.id || ''}
							readOnly
							placeholder="Artwork ID"
							style={{ backgroundColor: '#f5f5f5', color: '#888' }}
						/>
					</div>
					<div style={{ marginBottom: 16 }}>
						<Text strong>Title:</Text>
						<Input
							   value={selectedEditArtwork?.title || ''}
							readOnly
							placeholder="Artwork Title"
							style={{ backgroundColor: '#f5f5f5', color: '#888' }}
						/>
					</div>
					<div style={{ marginBottom: 16 }}>
						<Text strong>Placard ID:</Text>
						<Select
							showSearch
							value={editForm.placard_id}
							open={undefined}
							onChange={val => {
								handleEditFormChange('placard_id', val);
								if (document.activeElement instanceof HTMLElement) {
									document.activeElement.blur();
								}
							}}
							placeholder="Select Placard"
							style={{ width: '100%' }}
							optionFilterProp="children"
							filterOption={(input, option) =>
								String(option?.children || '').toLowerCase().includes(input.toLowerCase())
							}
						>
							{availablePlacards
								.filter((placard: Placards) =>
									!placard.artwork_id || placard.artwork_id === selectedEditArtwork.id
								)
								.map((placard: Placards) => (
									<Select.Option key={placard.code} value={placard.code}>
										{placard.code}
									</Select.Option>
								))}
						</Select>
					</div>
				</Modal>
				{/* Unlink Placard Confirmation Modal */}
				<Modal
					open={unlinkPlacardModal}
					title="Unlink Placard"
					onCancel={() => { setUnlinkPlacardModal(false); setUnlinkPlacardCode(null); }}
					footer={[
						<Button key="cancel" onClick={() => { setUnlinkPlacardModal(false); setUnlinkPlacardCode(null); }}>
							Cancel
						</Button>,
						<Button key="unlink" type="primary" danger loading={internalLoading} onClick={() => unlinkPlacard(unlinkPlacardCode!)}>
							Unlink
						</Button>,
					]}
				>
					<div>
						Are you sure that you want to unlink this artwork from the placard <b>{unlinkPlacardCode}</b>?
					</div>
				</Modal>
				{/* Modal for generated placards */}
				<Modal
					title="Generated placards"
					style={{ maxWidth: '800px' }}
					centered
					maskClosable={false}
					onCancel={() => setOpenModal(false)}
					open={openModal}
					footer={[
						<Button key="close" type="primary" onClick={() => setOpenModal(false)}>
							Close
						</Button>
					]}
					width={`100vw`}
				>
					<Flex justify='center' align='center'>
						<Button size='large' type="primary" className='mb-4' onClick={downloadPlacardAsPdf}>
							Download as PDF
						</Button>
					</Flex>
					<div style={{ overflow: 'auto', backgroundColor: 'transparent' }} ref={placardRef}>
						{generatedLinkedPlacs.length > 0 && generatedLinkedPlacs.map((newPlac, idx) => (
							<Placard key={idx} data={newPlac} />
						))}
					</div>
					<Flex justify='center' align='center'>
						<Button size='large' type="primary" className='mt-4' onClick={downloadPlacardAsPdf}>
							Download as PDF
						</Button>
					</Flex>
				</Modal>
				{/* Empty Placard modal */}
				<Modal
					title="Generated empty placards"
					style={{ maxWidth: '800px' }}
					centered
					onCancel={() => setOpenEmptyPlacardsModal(false)}
					open={openEmptyPlacardsModal}
					footer={[
						<Button key="cancel" onClick={() => setOpenEmptyPlacardsModal(false)}>Cancel</Button>,
						<Button key="generate" type="primary" onClick={() => { generateEmptyPlacards(false); }}>Generate placards</Button>,
						<Button key="export" disabled={times > 30} type="primary" onClick={() => { generateEmptyPlacards(true); }}>Generate & export</Button>
					]}
				>
					<div className='mt-4'>
						<Text strong>Number of placards</Text>
						<InputNumber className='mb-3' style={{ display: 'block', width: '126px' }} min={1} max={1000} defaultValue={times} value={times} onChange={(e) => setTimes(Number(e))} />
					</div>
					<Slider defaultValue={times} min={1} max={1000} value={times} onChange={(e) => setTimes(Number(e))} />
				</Modal>
			</S.PlacardsContainer>
		</>
	);
}
export default PlacardsScreen;